<template>
  <div class="member-dash">
    <div class="member-dash__inner">

      <!-- Header -->
      <div class="dash-hero">
        <div>
          <h1 class="dash-hero__title">Dashboard</h1>
          <p class="dash-hero__subtitle">Namirembe Diocese Young Christian SACCO</p>
        </div>
      </div>

      <!-- Bank Details -->
      <div class="info-card">
        <div class="info-card__icon"><i class="fas fa-university"></i></div>
        <div class="info-card__body">
          <h6 class="info-card__title">Bank Details</h6>
          <div class="info-card__chips">
            <div class="chip">
              <span class="chip__label">Bank</span>
              <span class="chip__value">Equity Bank</span>
            </div>
            <div class="chip">
              <span class="chip__label">Account name</span>
              <span class="chip__value">Namirembe Diocese Young Christian Sacco</span>
            </div>
            <div class="chip">
              <span class="chip__label">Account number</span>
              <span class="chip__value chip__value--mono">1039201443735</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Balances -->
      <h5 class="section-title">Account Balances</h5>
      <div class="balance-grid">
        <div class="balance-card">
          <span class="balance-card__accent" style="background:#0E7C66"></span>
          <div class="balance-card__top">
            <div class="balance-card__icon" style="color:#0E7C66; background:rgba(14,124,102,0.12)">
              <i class="fas fa-piggy-bank"></i>
            </div>
            <small class="balance-card__tag">Savings Account</small>
          </div>
          <div class="balance-card__amount">UGX {{ commas(savings) }}</div>
          <small class="balance-card__caption">Available Balance</small>
        </div>

        <div class="balance-card">
          <span class="balance-card__accent" style="background:#2952E3"></span>
          <div class="balance-card__top">
            <div class="balance-card__icon" style="color:#2952E3; background:rgba(41,82,227,0.12)">
              <i class="fas fa-chart-line"></i>
            </div>
            <small class="balance-card__tag">{{ number_of_shares }} Shares</small>
          </div>
          <div class="balance-card__amount">UGX {{ commas(shares) }}</div>
          <small class="balance-card__caption">Share Value</small>
        </div>

        <div class="balance-card">
          <span class="balance-card__accent" style="background:#C2540A"></span>
          <div class="balance-card__top">
            <div class="balance-card__icon" style="color:#C2540A; background:rgba(194,84,10,0.12)">
              <i class="fas fa-credit-card"></i>
            </div>
            <small class="balance-card__tag">Outstanding</small>
          </div>
          <div class="balance-card__amount">UGX {{ commas(loans) }}</div>
          <small class="balance-card__caption">Loan Balance</small>
        </div>
      </div>

      <!-- Transactions -->
      <div class="tx-card">
        <div class="tx-card__header">
          <div>
            <h6 class="tx-card__title">Recent Transactions</h6>
            <p class="tx-card__subtitle">Your latest account activity</p>
          </div>
          <div class="tx-card__filter">
            <label class="tx-card__filter-label">Show</label>
            <select v-model="items" class="tx-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="40">40</option>
              <option value="80">80</option>
            </select>
          </div>
        </div>

        <div v-if="!transactions" class="tx-loading">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="tx-loading__text">Loading transactions...</p>
        </div>

        <div v-else class="tx-list">
          <div v-for="(transaction, index) in visibleTransactions" :key="index" class="tx-row"
            :class="{ 'tx-row--alt': index % 2 === 0 }">
            <div class="tx-row__icon" :class="getTransactionIconClass(transaction.title)">
              <i :class="getTransactionIcon(transaction.title)"></i>
            </div>
            <div class="tx-row__body">
              <div class="tx-row__title">{{ transaction.title }}</div>
              <div class="tx-row__action">{{ transaction.action }}</div>
            </div>
            <div class="tx-row__date">{{ transaction.date_of_payment }}</div>
          </div>

          <div v-if="visibleTransactions.length === 0" class="tx-empty">
            No transactions to show yet.
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { transactionComputed, transactionMethods, savingComputed, savingMethods } from '@/state/helpers';

export default {
  name: "home",
  data() {
    return {
      items: 5,
      saving: '5000',
      shares: 0,
      number_of_shares: 0,
      loans: 0,
    }
  },

  computed: {
    ...transactionComputed,
    ...savingComputed,
    // Replaces the old v-if + v-for combo on the same element with a plain sliced list.
    visibleTransactions() {
      return this.transactions ? this.transactions.slice(0, this.items) : [];
    },
  },

  mounted() {
    this.dashboard();
    this.fetchTransactions();
    this.fetchSavings()
  },

  methods: {
    ...transactionMethods,
    ...savingMethods,
    commas(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    getTransactionIcon(title) {
      const icons = {
        'Shares': 'fas fa-chart-line',
        'Savings': 'fas fa-piggy-bank',
        'Dividend': 'fas fa-percentage',
        'Interest': 'fas fa-trending-up',
        'Withdraws': 'fas fa-arrow-down'
      };
      return icons[title] || 'fas fa-exchange-alt';
    },

    getTransactionIconClass(title) {
      const classes = {
        'Shares': 'tx-icon--primary',
        'Savings': 'tx-icon--success',
        'Dividend': 'tx-icon--info',
        'Interest': 'tx-icon--warning',
        'Withdraws': 'tx-icon--danger'
      };
      return classes[title] || 'tx-icon--secondary';
    },

    async dashboard() {
      try {
        const response = await axios.get('/dashboard');
        this.shares = response.data.shares;
        this.number_of_shares = response.data.number_of_shares
        this.loans = response.data.loans;
        this.number_of_shares = response.data.number_of_shares;
      } catch (error) {
        console.log(error);
      }
    },

    async getTransactions() {
      try {
        const response = await axios.get('/mytransactions');
        this.transactions = response.data.transactions;
      } catch (error) {
        console.log(error);
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');

.member-dash {
  --ink: #0B1220;
  --ink-soft: #64748B;
  --bg: #F5F7FA;
  --surface: #FFFFFF;
  --border: #E6E9EF;
  --accent: #0E7C66;
  --accent-soft: #E4F5F0;
  min-height: 100vh;
  background: var(--bg);
  font-family: 'Inter', sans-serif;
  color: var(--ink);
}

.member-dash__inner {
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 20px 48px;
}

/* ---------- Hero ---------- */
.dash-hero {
  margin-bottom: 20px;
}

.dash-hero__title {
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 1.6rem;
  margin: 0 0 4px;
}

.dash-hero__subtitle {
  color: var(--ink-soft);
  font-size: 0.9rem;
  margin: 0;
}

/* ---------- Bank details ---------- */
.info-card {
  display: flex;
  gap: 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 28px;
}

.info-card__icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.info-card__title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 0 0 10px;
}

.info-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.chip {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.chip__label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-soft);
  font-weight: 600;
}

.chip__value {
  font-size: 0.88rem;
  font-weight: 600;
}

.chip__value--mono {
  font-family: 'IBM Plex Mono', monospace;
}

/* ---------- Section title ---------- */
.section-title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 1.05rem;
  margin: 0 0 14px;
}

/* ---------- Balance cards ---------- */
.balance-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 28px;
}

@media (max-width: 760px) {
  .balance-grid {
    grid-template-columns: 1fr;
  }
}

.balance-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.balance-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(11, 18, 32, 0.08);
}

.balance-card__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}

.balance-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.balance-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.balance-card__tag {
  color: var(--ink-soft);
  font-size: 0.78rem;
}

.balance-card__amount {
  font-family: 'IBM Plex Mono', monospace;
  font-weight: 600;
  font-size: 1.4rem;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
}

.balance-card__caption {
  color: var(--ink-soft);
  font-size: 0.78rem;
}

/* ---------- Transactions ---------- */
.tx-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px;
}

.tx-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.tx-card__title {
  font-family: 'Sora', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 0 0 2px;
}

.tx-card__subtitle {
  color: var(--ink-soft);
  font-size: 0.8rem;
  margin: 0;
}

.tx-card__filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tx-card__filter-label {
  font-size: 0.78rem;
  color: var(--ink-soft);
}

.tx-select {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 0.82rem;
  background: var(--surface);
  color: var(--ink);
}

.tx-loading {
  text-align: center;
  padding: 40px 0;
}

.tx-loading__text {
  color: var(--ink-soft);
  font-size: 0.85rem;
  margin-top: 10px;
}

.tx-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tx-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  border-radius: 10px;
  transition: background 0.15s ease, transform 0.15s ease;
}

.tx-row--alt {
  background: var(--bg);
}

.tx-row:hover {
  background: var(--accent-soft);
  transform: translateX(2px);
}

.tx-row__icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #fff;
}

.tx-icon--primary { background: #2952E3; }
.tx-icon--success { background: #0E7C66; }
.tx-icon--info { background: #2AA6C9; }
.tx-icon--warning { background: #C2540A; }
.tx-icon--danger { background: #B3261E; }
.tx-icon--secondary { background: #64748B; }

.tx-row__body {
  flex: 1;
  min-width: 0;
}

.tx-row__title {
  font-weight: 600;
  font-size: 0.9rem;
}

.tx-row__action {
  color: var(--ink-soft);
  font-size: 0.78rem;
}

.tx-row__date {
  color: var(--ink-soft);
  font-size: 0.78rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.tx-empty {
  text-align: center;
  color: var(--ink-soft);
  font-size: 0.85rem;
  padding: 24px 0;
}

@media (max-width: 480px) {
  .tx-row__date {
    display: none;
  }
}
</style>